import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { State } from '../../../Flux/Slice/index.js'
import Actions from '../../../Flux/index.js'
import { orderSlice } from '../../../Flux/Slice/Order/OrderSlice.js'
import { useGetSamplePoolSelector } from '../../../Flux/Selector/index.js'
import type { FileForProcessing, IOrder } from '../../../Model/index.js'
import { SampleConfigurationTypes } from '../../../Generated/SampleConfigurationType.js'
import { useUppy } from '../../../Provider/UppyProvider.js'
import { planReversePairingByName, reverseFileCandidates } from '../../../Util/reverseFilePairing.js'
import type { BaseModalProps } from '../../Modal/BaseModal.js'
import { BaseModal } from '../../Modal/BaseModal.js'

export type AddReverseFileModalProps = {
  sample: FileForProcessing
  order: IOrder
}

/**
 * Gives one sample its missing R2: an upload of the order no sample uses, the file of a sample that is really
 * this one's R2 (absorbed), or a fresh upload.
 */
export const AddReverseFileModal = ({ isOpen, onClose, sample, order }: AddReverseFileModalProps & Omit<BaseModalProps, 'children'>) => {
  const dispatch = useDispatch()
  const { uppy } = useUppy()
  const uploads = useSelector((state: typeof State) => state.order.fileList)
  const uploadsError = useSelector((state: typeof State) => state.order.fileListError)
  const pools = useGetSamplePoolSelector()
  const [picked, setPicked] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setPicked(null)
      dispatch(Actions[orderSlice.name].loadFiles({ orderId: order.id }))
    }
  }, [isOpen, order.id, dispatch])

  const liveItems = useMemo(() => (pools === 'LOADING' ? [] : pools.flatMap((pool) => [...pool.samples, ...pool.negativeControls])), [pools])
  const candidates = useMemo(
    () => reverseFileCandidates(sample, uploads ?? [], liveItems, order.sampleConfiguration),
    [sample, uploads, liveItems, order.sampleConfiguration]
  )
  const suggested = useMemo(() => planReversePairingByName([sample], () => candidates)[0]?.fileId, [sample, candidates])
  const ordered = [...candidates].sort((a, b) => Number(b.fileId === suggested) - Number(a.fileId === suggested))
  const selected = picked ?? suggested ?? null
  const name = sample.customerSampleName || sample.forwardFile?.fileName || 'this sample'

  const attach = () => {
    const candidate = candidates.find((c) => c.fileId === selected)
    if (!candidate || !sample.poolId) return
    dispatch(
      Actions[orderSlice.name].attachReverseFileRequest({
        orderId: order.id,
        poolId: sample.poolId,
        sampleId: sample.id,
        reverseFileId: candidate.fileId,
        absorbSampleId: candidate.absorbSample?.id
      })
    )
    onClose()
  }

  const upload = (file: File | undefined) => {
    if (!file || !uppy || !sample.poolId) return
    uppy.addFile({
      name: file.name,
      type: file.type,
      data: file,
      meta: {
        orderId: order.id,
        poolId: sample.poolId,
        itemType: sample.itemType,
        isMerged: false,
        isTrimmed: SampleConfigurationTypes[order.sampleConfiguration].impliesTrimmed,
        attachReverseTo: sample.id
      }
    })
    onClose()
  }

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} testId="add-reverse-file-modal">
      <div className="modal-box max-w-2xl">
        <h3 className="text-lg font-bold">Add the R2 file for {name}</h3>
        <p className="mt-2 text-sm text-base-content/70">
          This order expects an R1/R2 pair per sample, and this sample only has its R1 file ({sample.forwardFile?.fileName}). Pick its R2
          from the files already uploaded, or upload it.
        </p>

        <div className="mt-4">
          {uploadsError ? (
            <div className="alert alert-error text-sm">The order&apos;s files could not be loaded. You can still upload the R2 below.</div>
          ) : !uploads ? (
            <div className="text-sm">Loading files…</div>
          ) : ordered.length === 0 ? (
            <div className="text-sm text-base-content/60">No uploaded file is free to use as this sample&apos;s R2.</div>
          ) : (
            <ul className="max-h-64 space-y-1 overflow-y-auto">
              {ordered.map((candidate) => (
                <li key={candidate.fileId}>
                  <label className="flex cursor-pointer items-start gap-2 rounded p-1 hover:bg-base-200">
                    <input
                      type="radio"
                      className="radio radio-sm mt-0.5"
                      name={`reverse-file-${sample.id}`}
                      checked={selected === candidate.fileId}
                      onChange={() => setPicked(candidate.fileId)}
                      data-testid={`reverse-file-candidate-${candidate.fileId}`}
                    />
                    <span className="text-sm">
                      <span className="font-mono">{candidate.fileName}</span>
                      {candidate.fileId === suggested && <span className="badge badge-success badge-sm ml-2">matches by name</span>}
                      {candidate.absorbSample && (
                        <span className="block text-xs text-warning">
                          Currently its own sample{candidate.absorbSample.name ? ` (${candidate.absorbSample.name})` : ''} — that sample is
                          removed and its file becomes this sample&apos;s R2.
                        </span>
                      )}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="modal-action flex-wrap items-center justify-between gap-2">
          <label className="btn btn-sm btn-outline">
            Upload the R2…
            <input
              type="file"
              accept=".gz"
              className="hidden"
              data-testid="reverse-file-upload"
              onChange={(e) => upload(e.target.files?.[0])}
            />
          </label>
          <div className="flex gap-2">
            <button type="button" className="btn btn-sm" onClick={() => onClose()}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={!selected}
              onClick={attach}
              data-testid="attach-reverse-file"
            >
              Add as R2
            </button>
          </div>
        </div>
      </div>
    </BaseModal>
  )
}
